import React from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { NamedLink } from '../../components';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './PricingPage.module.css';

// ============================================
// PRICING DATA - EDIT PRICES HERE
// ============================================
export const PRICING_DATA = [
  {
    title: 'Cars\nStandard',
    subtitle: 'The essential package to get your Porsche in front of dedicated buyers.',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
    price: 'Free to list',
    isFree: true,
    bgColor: '#ff6633',
  },
  {
    title: 'Cars\nFeatured',
    subtitle: 'Boost your visibility. Ideal for faster sales and higher-value Porsches.',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'Prime Homepage Placement',
    ],
    price: '£24.95',
    isFree: false,
    bgColor: '#ff6633',
  },
  {
    title: 'Cars\nPremium',
    subtitle: 'Maximum on-site reach. Dominate the homepage and our social feeds.',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'VIP Homepage Priority',
      'Rennzone Social Media Boost',
    ],
    price: '£79.95',
    isFree: false,
    bgColor: '#ff6633',
  },
  {
    title: 'Cars\nPremium Pro',
    subtitle: 'The ultimate selling tool. We actively target high-value buyers via Google Ads.',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'VIP Homepage Priority',
      'Rennzone Social Media Boost',
      'Targeted Google Ads Campaign',
    ],
    price: '£299.95',
    isFree: false,
    bgColor: '#ff6633',
  },
  {
    title: 'New & Used Parts',
    subtitle: '',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
    price: 'Free to list',
    isFree: true,
    bgColor: '#ff6633',
  },
  {
    title: 'Books, Art & Clothing',
    subtitle: '',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
    price: 'Free to list',
    isFree: true,
    bgColor: '#ff6633',
  },
  {
    title: 'Watches & Memorabilia',
    subtitle: '',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
    price: 'Free to list',
    isFree: true,
    bgColor: '#ff6633',
  },
  {
    title: 'Models & Toys',
    subtitle: '',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
    price: 'Free to list',
    isFree: true,
    bgColor: '#ff6633',
  },
];

// ============================================
// PRICING CARD COMPONENT
// ============================================
function PricingCard({ title, subtitle, features, price, bgColor }) {
  const titleLines = title.split('\n');

  return (
    <div className={css.pricingCard}>
      {/* Title Box */}
      <div className={css.pricingCardTitle} style={{ backgroundColor: bgColor }}>
        {titleLines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>

      {/* Card Body */}
      <div className={css.pricingCardBody}>
        {subtitle ? (
          <div className={css.pricingCardSubtitle}>{subtitle}</div>
        ) : null}

        {/* Features */}
        <div className={css.pricingCardFeatures}>
          {features.map((feature, index) => (
            <div
              key={index}
              className={index % 2 === 0 ? css.featureRowEven : css.featureRowOdd}
            >
              {feature.split(' ').map((word, wi) => {
                const isBold = word === 'Free' || word === 'Unlimited';
                return (
                  <span key={wi} className={isBold ? css.featureBold : undefined}>
                    {wi > 0 ? ' ' : ''}
                    {word}
                  </span>
                );
              })}
            </div>
          ))}
        </div>

        <div className={css.pricingCardDivider} />

        {/* Price */}
        <div className={css.pricingCardPrice}>{price}</div>

        {/* CTA */}
        <NamedLink name="NewListingPage" className={css.pricingCardCta}>
          Create a Listing
        </NamedLink>
      </div>
    </div>
  );
}

// ============================================
// BULLET LIST HELPER
// ============================================
function BulletList({ items }) {
  return (
    <ul className={css.bulletList}>
      {items.map((item, i) => (
        <li key={i} className={css.bulletItem}>
          <span className={css.bulletDot}>•</span>
          <span>
            {item.label ? <strong>{item.label}</strong> : null}
            {item.label ? ' ' : ''}
            {item.text || item}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================
export const PricingPageComponent = props => {
  const { scrollingDisabled } = props;

  const topRowPricing = PRICING_DATA.slice(0, 4);
  const bottomRowPricing = PRICING_DATA.slice(4, 8);

  return (
    <Page
      title="Pricing Plans"
      description="Sell Your Porsche on Rennzone — the dedicated Porsche marketplace."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.layoutWrapperMain}
      >
        <main className={css.mainContent}>

          {/* ── Header ── */}
          <div className={css.headerSection}>
            <h2 className={css.headerTitle}>Sell Your Porsche on Rennzone.</h2>
            <p className={css.headerSubtitleBold}>
              The world's dedicated Porsche classifieds for cars, parts, and accessories.
              Experience a quicker way to sell with a marketplace optimized for the Porsche
              community.
            </p>
            <p className={css.headerSubtitle}>
              List your Porsche for free today and reach thousands of verified enthusiasts.
            </p>
          </div>

          {/* ── Cars pricing row ── */}
          <div className={css.gridSection}>
            <div className={css.pricingGrid}>
              {topRowPricing.map((pricing, index) => (
                <PricingCard key={index} {...pricing} />
              ))}
            </div>
          </div>

          {/* ── Other items pricing row ── */}
          <div className={css.gridSection}>
            <div className={css.pricingGrid}>
              {bottomRowPricing.map((pricing, index) => (
                <PricingCard key={index} {...pricing} />
              ))}
            </div>
          </div>

          {/* ── Trade Sellers ── */}
          <div className={css.sectionWrapper}>
            <div className={css.whiteCard}>
              <p className={css.tradeEyebrow}>Porsche Trade Sellers</p>
              <h3 className={css.tradeTitle}>
                A Dedicated Marketplace for Porsche Professionals
              </h3>
              <p className={css.tradeLead}>
                Stop paying mass-market prices for low-quality leads. Rennzone offers bespoke
                trade packages designed to move specialist inventory faster and connect you with
                a purist audience.
              </p>
              <p className={css.tradeBody}>
                Get started today with a <strong>3-month complimentary trial period.</strong>{' '}
                Once you get in touch, a personal{' '}
                <strong>Rennzone Account Manager</strong> will be assigned to your dealership to
                streamline your experience. To ensure you hit the ground running,{' '}
                <strong>
                  we will personally set up and optimize your initial listings for you
                </strong>
                , saving you time while maximizing your visibility.
              </p>
              <p className={css.tradeBody}>
                From performance insights to dedicated support, we provide the tools you need to
                sell more effectively.{' '}
                <strong>Ready to showcase your showroom to the right buyers?</strong>
              </p>
              <button className={css.ctaOrange}>Contact Us</button>
            </div>
          </div>

          {/* ── Why Sell on Rennzone ── */}
          <div className={css.sectionWrapper}>
            <div className={css.whiteCard}>
              <h2 className={css.whySellTitle}>Why Sell on Rennzone?</h2>
              <p className={css.whySellSubtitle}>
                The Premier Porsche Marketplace for Enthusiasts
              </p>
              <p className={css.bodyText}>
                When it comes to selling a Porsche, the platform you choose is just as important
                as the car itself. General automotive marketplaces are often flooded with
                "tire-kickers," irrelevant inquiries, and high listing fees that eat into your
                return. Rennzone was built to solve this. We are a 100% unique, dedicated Porsche
                marketplace designed specifically for the community, by the community.
              </p>
              <p className={css.bodyTextSpaced}>
                Whether you are looking to sell a classic air-cooled 911, find a new home for a
                modern GT3 RS, or clear out rare Porsche parts from your garage, Rennzone
                provides the precision-targeted audience you need.
              </p>

              <div className={css.twoColGrid}>

                {/* A Marketplace for Everything Porsche */}
                <div>
                  <h3 className={css.featureSectionTitle}>
                    A Marketplace for Everything Porsche
                  </h3>
                  <p className={css.bodyText}>
                    Rennzone isn't just about the cars; it's about the entire Porsche lifestyle.
                    While other platforms focus on generic transport, we dive deep into the
                    Stuttgart DNA. On Rennzone, you can list:
                  </p>
                  <BulletList
                    items={[
                      {
                        label: 'Porsche Vehicles:',
                        text: 'From the legendary Porsche 356 and 911 to the Cayman, Boxster, Macan, Cayenne, and the all-electric Taycan.',
                      },
                      {
                        label: 'Genuine Porsche Parts:',
                        text: "Whether it's a set of Fuchs wheels, a period-correct 964 interior, or high-performance aftermarket upgrades.",
                      },
                      {
                        label: 'Porsche Memorabilia:',
                        text: "Rare posters, dealership signage, owner's manuals, and limited-edition watches.",
                      },
                    ]}
                  />
                </div>

                {/* Reach a Targeted Audience */}
                <div>
                  <h3 className={css.featureSectionTitle}>
                    Reach a Targeted Audience of Porsche Purists
                  </h3>
                  <p className={css.bodyText}>
                    The biggest challenge of selling a high-value vehicle on a mass-market site
                    is the "noise." On Rennzone, your listing is placed directly in front of
                    Porsche enthusiasts, collectors, and serious buyers. Our users aren't just
                    looking for "a car"—they are looking for a specific chassis code, a certain
                    paint-to-sample (PTS) colour, or a particular engine configuration.
                  </p>
                  <p className={css.bodyText}>
                    By listing on a niche marketplace, you skip the generic questions and engage
                    immediately with people who understand the value of a documented service
                    history and the significance of a "matching numbers" classic.
                  </p>
                </div>

                {/* SEO-Optimized Listings */}
                <div>
                  <h3 className={css.featureSectionTitle}>
                    SEO-Optimized Listings for Maximum Visibility
                  </h3>
                  <p className={css.bodyText}>
                    We have engineered Rennzone to be a powerhouse for search engine
                    optimisation. When you create a listing on our platform, our backend works
                    to ensure your car or part is discoverable on Google. We target high-intent
                    keywords such as:
                  </p>
                  <BulletList
                    items={[
                      'Used Porsche 911 for sale',
                      'Air-cooled Porsche marketplace',
                      'Porsche GT cars for sale',
                      'Rare Porsche parts online',
                    ]}
                  />
                  <p className={css.bodyText}>
                    Our Premium and Premium Pro packages take this a step further, utilising
                    dedicated Google Ads and social media exposure to ensure your Porsche is
                    seen by the right eyeballs globally, not just locally.
                  </p>
                </div>

                {/* The Problem with Traditional Classifieds */}
                <div>
                  <h3 className={css.featureSectionTitle}>
                    The Problem with Traditional Classifieds
                  </h3>
                  <p className={css.bodyText}>
                    Many sellers default to Facebook Marketplace or eBay, only to be met with
                    frustration. Facebook is riddled with "Is this still available?" bots, and
                    eBay's fee structure can be prohibitively expensive for high-ticket items.
                  </p>
                  <p className={css.bodyText}>
                    Rennzone offers a Standard package that is{' '}
                    <strong>Free to List</strong>, allowing you to test the waters of the
                    enthusiast market without any upfront financial risk. We believe in
                    democratising the Porsche selling process. You shouldn't have to pay a
                    premium just to show your car to people who love the brand as much as you do.
                  </p>
                </div>

                {/* Performance Analytics */}
                <div>
                  <h3 className={css.featureSectionTitle}>
                    Performance Analytics and Professional Tools
                  </h3>
                  <p className={css.bodyText}>
                    Selling a Porsche is a professional transaction, and you deserve professional
                    tools. Our Track Listing Analytics (available on all tiers) allow you to see
                    exactly how many people are viewing your listing, how many have bookmarked
                    it, and where your traffic is coming from.
                  </p>
                  <p className={css.bodyText}>
                    For those selling high-value assets like a Porsche 911 GT2 RS or a Carrera
                    GT, our Premium Pro tier provides a Targeted Google Ads Campaign. This means
                    we run a bespoke ad campaign specifically for your car, targeting buyers who
                    are actively searching for that exact model. It is the closest thing to
                    having a private broker, but at a fraction of the cost.
                  </p>
                </div>

                {/* Built for the Modern Enthusiast */}
                <div>
                  <h3 className={css.featureSectionTitle}>
                    Built for the Modern Enthusiast
                  </h3>
                  <p className={css.bodyText}>
                    The Porsche world is moving fast. With the rise of digital auctions and
                    global shipping, the market is no longer confined to your local town.
                    Rennzone is built on a modern tech stack that is fast, mobile-friendly, and
                    secure.
                  </p>
                  <BulletList
                    items={[
                      {
                        label: 'Walkaround Video Uploads:',
                        text: 'Give buyers a virtual cold-start video or a walk-around tour to build trust.',
                      },
                      {
                        label: 'Secure Buyer Messaging:',
                        text: 'Communicate with potential buyers through our encrypted platform to keep your personal data safe.',
                      },
                      {
                        label: 'Unlimited High-Res Photos:',
                        text: "Unlike other sites that compress your images, we allow unlimited high-quality uploads so you can showcase every curve of that Guards Red paintwork.",
                      },
                    ]}
                  />
                </div>

              </div>

              {/* Supporting the Community */}
              <div className={css.communitySection}>
                <h3 className={css.featureSectionTitle}>
                  Supporting the Porsche Community
                </h3>
                <p className={css.bodyTextSpaced}>
                  At the heart of Rennzone is a passion for the brand's heritage. We understand
                  the difference between a 996, 997, 991, and 992. We know why a G-Series
                  Carrera is special and why the transaxle era of the 928 and 944 is seeing a
                  massive resurgence. When you sell on Rennzone, you aren't just a "user ID"—you
                  are a member of a community. We actively promote our sellers on our social
                  feeds, connecting you with thousands of followers who live and breathe Porsche.
                </p>

                {/* Bottom CTA */}
                <div className={css.ctaBox}>
                  <h3 className={css.ctaBoxTitle}>
                    Why Wait? Your Porsche's New Owner is Here.
                  </h3>
                  <p className={css.bodyText}>
                    Whether you're a private collector thinning out a fleet or a specialist
                    dealer looking for a more cost-effective way to reach buyers, Rennzone is
                    the answer. Our platform is the "Turbo" button for your sales process.
                  </p>
                  <p className={css.bodyTextSpaced}>
                    Stop settling for generic marketplaces that don't understand your car. Give
                    your Porsche the specialised platform it deserves. From the first click to
                    the final handshake, Rennzone is here to ensure the transition of your
                    Porsche is as smooth as a PDK gear change.
                  </p>
                  <p className={css.ctaBoxLead}>
                    Ready to get started? Create your listing in under five minutes and join the
                    UK's fastest-growing Porsche marketplace.
                  </p>
                  <NamedLink name="NewListingPage" className={css.ctaOrange}>
                    Create a Free Listing
                  </NamedLink>
                </div>
              </div>

            </div>
          </div>

        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

PricingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const mapStateToProps = state => ({
  scrollingDisabled: state.ui?.scrollingDisabled || false,
});

const PricingPage = compose(connect(mapStateToProps))(PricingPageComponent);

export default PricingPage;
